// src/pages/ExpertsDirectory.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

export default function ExpertsDirectory() {
  const { user } = useAuth() || {};
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [experts, setExperts] = useState([]);
  const [expertImages, setExpertImages] = useState({});

  // Mission / contact
  const [showMissionForm, setShowMissionForm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // (on conserve formData pour éviter de casser des dépendances futures)
  const [formData, setFormData] = useState({
    subject: "consultation",
    artwork: "",
    message: "",
  });

  const isArtkivExpert = (e) => !!e?.expert_user_id;

  const ArtkivBadge = () => (
    <div className="absolute top-3 left-3 z-10 pointer-events-none">
      <span className="inline-flex items-center rounded-full bg-black/90 text-white text-xs px-2.5 py-1 shadow">
        {t("expertsDirectory.badge")}
      </span>
    </div>
  );



  // --------------------------------
  // Helpers conversation / messages
  // --------------------------------
  const getMyDisplayName = async (fallbackEmail) => {
    try {
      if (!user?.id) return fallbackEmail || t("expertsDirectory.requestLabel");
      // RPC utilisée ailleurs pour éviter un select direct sur profiles
      const { data, error } = await supabase.rpc("get_profile_basics", { p_user_ids: [user?.id] });
      if (error) throw error;
      const p = Array.isArray(data) ? data[0] : null;
      const full = `${p?.first_name || ""} ${p?.last_name || ""}`.trim();
      return full || fallbackEmail || t("expertsDirectory.requestLabel");
    } catch (_) {
      return fallbackEmail || t("expertsDirectory.requestLabel");
    }
  };

  const ensureConversationAndSeedMessages = async ({
    clientId,
    clientEmail,
    expertUserId,
    expertClientId,
    introText,
  }) => {
    // 1) conversation (liée à la demande)
    const clientLabel = await getMyDisplayName(clientEmail);
    const subject = `${t("expertsDirectory.requestLabel")} • ${clientLabel}`;

    // On crée une conversation dédiée à cette demande (context_type + context_id)
    const { data: conv, error: cErr } = await supabase
      .from("conversations")
      .insert([
        {
          subject,
          expert_id: expertUserId,
          client_id: clientId,
          context_type: "advisor_request",
          context_id: expertClientId,
        },
      ])
      .select("id")
      .single();
    if (cErr) throw cErr;

    const conversationId = conv?.id;
    if (!conversationId) throw new Error(t("expertsDirectory.conversationError"));

    // 2) participants (best effort, selon votre schéma)
    // Si vous avez déjà un trigger DB qui le fait, ces inserts seront ignorés ou pourront échouer sans casser.
    try {
      await supabase.from("conversation_participants").insert([
        { conversation_id: conversationId, user_id: clientId },
        { conversation_id: conversationId, user_id: expertUserId },
      ]);
    } catch (_) {}

    // 3) seed messages: au minimum un message pour éviter le thread vide
    const cleanIntro = (introText || "").trim();
    const seed = cleanIntro
      ? [{ conversation_id: conversationId, sender_id: clientId, body: cleanIntro }]
      : [
          {
            conversation_id: conversationId,
            sender_id: clientId,
            body: t("expertsDirectory.defaultIntro"),
          },
        ];

    const { error: mErr } = await supabase.from("messages").insert(seed);
    if (mErr) throw mErr;

    // 4) updated_at (si vous n'avez pas un trigger, on force)
    try {
      await supabase
        .from("conversations")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", conversationId);
    } catch (_) {}

    return conversationId;
  };

  // --------------------------------
  // Load experts + images
  // --------------------------------
  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from("experts").select("*").order("name");

      if (error) {
        console.error(error);
        return;
      }

      setExperts(data || []);

      const imgs = {};
      for (const e of data || []) {
        if (!e.image_url) {
          imgs[e.id] = null;
          continue;
        }
        if (e.image_url.startsWith("http")) {
          imgs[e.id] = e.image_url;
          continue;
        }
        const { data: signed } = await supabase.storage.from("experts").createSignedUrl(e.image_url, 3600);
        imgs[e.id] = signed?.signedUrl || null;
      }
      setExpertImages(imgs);
    };
    load();
  }, []);

  // --------------------------------
  // Filters
  // --------------------------------
  const specialties = useMemo(() => ["all", ...new Set(experts.map((e) => e.specialty).filter(Boolean))], [experts]);

  const filteredExperts = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return experts.filter((e) => {
      const okSearch = (e.name || "").toLowerCase().includes(q) || (e.specialty || "").toLowerCase().includes(q);
      const okSpec = selectedSpecialty === "all" || e.specialty === selectedSpecialty;
      return okSearch && okSpec;
    });
  }, [experts, searchTerm, selectedSpecialty]);

  // --------------------------------
  // Devenir conseiller personnel
  // --------------------------------
  const requestPersonalAdvisor = async (expert) => {
    if (!user?.id) {
      alert(t("expertsDirectory.loginRequired"));
      return;
    }
    if (!isArtkivExpert(expert)) {
      alert(t("expertsDirectory.artkivOnly"));
      return;
    }

    setIsSending(true);

    try {
      const { data: auth } = await supabase.auth.getUser();
      const clientId = auth?.user?.id;
      const clientEmail = auth?.user?.email;
      if (!clientId) throw new Error(t("expertsDirectory.notAuthenticated"));

      // anti-duplicate
      const { data: existing } = await supabase
        .from("expert_clients")
        .select("id")
        .eq("expert_id", expert.expert_user_id)
        .eq("invited_email", clientEmail)
        .in("status", ["pending", "accepted"])
        .maybeSingle();

      if (existing) {
        alert(t("expertsDirectory.alreadyRequested"));
        return;
      }

      // 1️⃣ expert_clients (source de vérité)
      const { data: ec, error } = await supabase
        .from("expert_clients")
        .insert([
          {
            expert_id: expert.expert_user_id,
            client_id: clientId,
            invited_email: clientEmail,
            message: formData.message || null,
            status: "pending",
          },
        ])
        .select("id")
        .single();

      if (error) throw error;

      // ✅ 1bis) conversation + message d'introduction (votre "messagerie temps réel")
      // IMPORTANT: on seed au moins 1 message, sinon le thread reste vide côté expert et côté client.
      let conversationId = null;
      try {
        conversationId = await ensureConversationAndSeedMessages({
          clientId,
          clientEmail,
          expertUserId: expert.expert_user_id,
          expertClientId: ec.id,
          introText: formData.message,
        });
      } catch (convErr) {
        // On ne bloque pas l'invitation si la conversation échoue, mais on log.
        console.error("[experts] conversation seed failed", convErr);
      }

      // 2️⃣ trace kanban (optionnelle, non bloquante)
      await supabase.from("expert_service_requests").insert([
        {
          expert_id: expert.expert_user_id,
          client_id: clientId,
          service_type: "custom",
          description: `[INVITATION_EXPERT_CLIENT_ID:${ec.id}] ${t("expertsDirectory.requestDescription")}`, 
          status: "pending",
          amount_ht: 0,
          amount_ttc: 0,
          artkiv_commission: 0,
          expert_net_amount: 0,
          metadata: {
            source: "expert_invitation",
            expert_client_id: ec.id,
            conversation_id: conversationId,
          },
        },
      ]);

      setSuccessMessage(t("expertsDirectory.success"));
      setTimeout(() => setSuccessMessage(""), 3000);
      setShowMissionForm(false);

      // Option UX: ouvrir directement la messagerie sur le thread si on a l'ID
      if (conversationId) {
        // navigate(`/messages?conversation=${conversationId}`);
      }
    } catch (e) {
      console.error(e);
      alert(t("expertsDirectory.sendError"));
    } finally {
      setIsSending(false);
    }
  };

  // --------------------------------
  // UI
  // --------------------------------
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-12">
      <h1 className="font-light mb-8" style={{ fontSize: "clamp(26px, 7vw, 42px)" }}>{t("expertsDirectory.title")}</h1>

      <div className="mb-6">
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("expertsDirectory.search")}
          className="w-full border px-4 py-3 rounded"
        />
      </div>

      <div
        className="flex gap-2 mb-8 overflow-x-auto"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
      >
        {specialties.map((s) => (
          <button
            key={s}
            onClick={() => setSelectedSpecialty(s)}
            className={`flex-shrink-0 px-3 py-2 rounded text-sm ${selectedSpecialty === s ? "bg-black text-white" : "bg-gray-100"}`}
          >
            {s === "all" ? t("expertsDirectory.allSpecialties") : s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {filteredExperts.map((expert) => (
          <div
            key={expert.id}
            className="border rounded-lg overflow-hidden cursor-pointer"
            onClick={() => setSelectedExpert(expert)}
          >
            {/* ✅ Image + badge */}
            <div className="bg-gray-100 relative" style={{ height: "clamp(140px, 35vw, 224px)" }}>
              {isArtkivExpert(expert) && <ArtkivBadge />}

              {expertImages[expert.id] && (
                <img src={expertImages[expert.id]} alt={expert.name} className="w-full h-full object-cover" />
              )}
            </div>

            <div className="p-4">
              <h3 className="font-serif" style={{ fontSize: "clamp(14px, 4vw, 20px)" }}>{expert.name}</h3>
              <p className="text-sm text-gray-600">{expert.specialty}</p>
            </div>
          </div>
        ))}
      </div>

      {/* MODAL */}
      {selectedExpert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white max-w-2xl w-full mx-4 p-6 md:p-8 relative max-h-[90vh] overflow-y-auto">
            <button className="absolute top-4 right-4" onClick={() => setSelectedExpert(null)}>
              <X />
            </button>

            <h2 className="font-serif mb-2" style={{ fontSize: "clamp(20px, 6vw, 30px)" }}>{selectedExpert.name}</h2>

            {isArtkivExpert(selectedExpert) && (
              <div className="mb-3">
                <span className="inline-flex items-center rounded-full bg-black/90 text-white text-xs px-2.5 py-1">
                  {t("expertsDirectory.badge")}
                </span>
              </div>
            )}

            <p className="mb-4">{selectedExpert.specialty}</p>

            {successMessage && <div className="mb-4 text-green-700">{successMessage}</div>}

            {isArtkivExpert(selectedExpert) && (
              <>
                {!showMissionForm ? (
                  <button className="border px-4 py-2" onClick={() => setShowMissionForm(true)}>
                    {t("expertsDirectory.becomeAdvisor")}
                  </button>
                ) : (
                  <>
                    <textarea
                      className="w-full border p-3 mb-4"
                      placeholder={t("expertsDirectory.messagePlaceholder")}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                    <button
                      onClick={() => requestPersonalAdvisor(selectedExpert)}
                      disabled={isSending}
                      className="bg-black text-white px-4 py-2"
                    >
                      {isSending ? t("expertsDirectory.sending") : t("expertsDirectory.send")}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}